import { API, Auth } from 'aws-amplify'

const API_NAME = 'DonationsAPI'

// Helper to get auth headers
const getAuthHeaders = async () => {
  const session = await Auth.currentSession()
  return {
    Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
  }
}

// Campaign status enum
export type CampaignStatus = 'active' | 'paused' | 'completed' | 'ended'

// API response types (snake_case from API)
export interface CampaignApiResponse {
  id: string
  title: string
  target_amount: number
  initial_amount: number  // Seed amount added at campaign start
  raised_amount: number   // Amount raised from tracked transactions
  total_raised: number    // Computed: initial_amount + raised_amount
  status: CampaignStatus
  is_active: boolean      // Computed: true if status is "active"
  end_date?: string // YYYY-MM-DD format
  linked_donation_type_ids: string[]
  is_default: boolean     // If true, this campaign is shown when no donation types are selected
  color: string           // Hex color code (e.g. "#4A7C59")
  created_at: string
  updated_at: string
}

export interface CampaignCreateRequest {
  title: string
  target_amount: number
  initial_amount: number  // Seed amount (not from tracked transactions)
  status?: CampaignStatus
  end_date?: string // YYYY-MM-DD format
  linked_donation_type_ids: string[]
  is_default?: boolean    // If true, this campaign is shown when no donation types are selected
  color?: string          // Hex color code (e.g. "#4A7C59")
}

export interface CampaignUpdateRequest {
  title?: string
  target_amount?: number
  initial_amount?: number  // Seed amount can be updated
  status?: CampaignStatus
  end_date?: string // YYYY-MM-DD format, empty string to clear
  linked_donation_type_ids?: string[]
  is_default?: boolean    // If true, this campaign is shown when no donation types are selected
  color?: string          // Hex color code (e.g. "#4A7C59")
}

// Analytics types (snake_case from API)
export interface DonationTypeBreakdownApi {
  donation_type_id: string
  amount: number
  count: number
}

export interface PlatformBreakdownApi {
  platform: string
  amount: number
  count: number
}

export interface CampaignAnalyticsApiResponse {
  campaign_id: string
  period: string
  start_date?: string
  end_date?: string
  total_amount: number
  raised_amount: number
  total_transactions: number
  by_donation_type: DonationTypeBreakdownApi[]
  by_platform: PlatformBreakdownApi[]
}

// Frontend analytics types (camelCase)
export interface DonationTypeBreakdown {
  donationTypeId: string
  amount: number
  count: number
}

export interface PlatformBreakdown {
  platform: string
  amount: number
  count: number
}

export interface CampaignAnalytics {
  campaignId: string
  period: string
  startDate?: string
  endDate?: string
  totalAmount: number
  raisedAmount: number
  totalTransactions: number
  byDonationType: DonationTypeBreakdown[]
  byPlatform: PlatformBreakdown[]
}

// Frontend type (camelCase)
export interface Campaign {
  id: string
  title: string
  targetAmount: number
  initialAmount: number   // Seed amount added at campaign start
  raisedAmount: number    // Amount raised from tracked transactions (read-only)
  totalRaised: number     // Computed: initial_amount + raised_amount (read-only)
  status: CampaignStatus
  isActive: boolean       // Computed: true if status is "active" (read-only)
  endDate?: string // YYYY-MM-DD format
  linkedDonationTypeIds: string[]
  isDefault: boolean      // If true, this campaign is shown when no donation types are selected
  color: string           // Hex color code (e.g. "#4A7C59")
  createdAt?: string
  updatedAt?: string
}

// Transform API response to frontend type
const transformApiResponse = (response: CampaignApiResponse): Campaign => ({
  id: response.id,
  title: response.title,
  targetAmount: response.target_amount,
  initialAmount: response.initial_amount,
  raisedAmount: response.raised_amount,
  totalRaised: response.total_raised ?? (response.initial_amount + response.raised_amount),
  // Fallback for backwards compatibility with old data
  status: response.status,
  isActive: response.is_active,
  endDate: response.end_date,
  linkedDonationTypeIds: response.linked_donation_type_ids || [],
  isDefault: response.is_default ?? false,
  color: response.color || '#4A7C59', // Default green if not set
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

// Transform frontend type to API request
const transformToApiRequest = (campaign: Campaign): CampaignCreateRequest => ({
  title: campaign.title,
  target_amount: campaign.targetAmount,
  initial_amount: campaign.initialAmount,
  status: campaign.status,
  end_date: campaign.endDate || undefined, // Only include if set (for create)
  linked_donation_type_ids: campaign.linkedDonationTypeIds,
  is_default: campaign.isDefault,
  color: campaign.color,
})

export const campaignsApi = {
  // List all campaigns
  async listCampaigns(activeOnly: boolean = false): Promise<Campaign[]> {
    const path = activeOnly ? '/campaigns?active=true' : '/campaigns'
    const headers = await getAuthHeaders()
    const response: CampaignApiResponse[] = await API.get(API_NAME, path, { headers })
    return response.map(transformApiResponse)
  },

  // Get a single campaign by ID
  async getCampaign(id: string): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const response: CampaignApiResponse = await API.get(API_NAME, `/campaigns/${id}`, { headers })
    return transformApiResponse(response)
  },

  // Create a new campaign
  async createCampaign(campaign: Campaign): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const body = transformToApiRequest(campaign)
    const response: CampaignApiResponse = await API.post(API_NAME, '/campaigns', { headers, body })
    return transformApiResponse(response)
  },

  // Update an existing campaign
  async updateCampaign(id: string, updates: Partial<Campaign>): Promise<Campaign> {
    const headers = await getAuthHeaders()
    const body: CampaignUpdateRequest = {}

    if (updates.title !== undefined) body.title = updates.title
    if (updates.targetAmount !== undefined) body.target_amount = updates.targetAmount
    if (updates.initialAmount !== undefined) body.initial_amount = updates.initialAmount
    if (updates.status !== undefined) body.status = updates.status
    if (updates.endDate !== undefined) body.end_date = updates.endDate
    if (updates.linkedDonationTypeIds !== undefined) body.linked_donation_type_ids = updates.linkedDonationTypeIds
    if (updates.isDefault !== undefined) body.is_default = updates.isDefault
    if (updates.color !== undefined) body.color = updates.color

    const response: CampaignApiResponse = await API.put(API_NAME, `/campaigns/${id}`, { headers, body })
    return transformApiResponse(response)
  },

  // Delete a campaign
  async deleteCampaign(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    await API.del(API_NAME, `/campaigns/${id}`, { headers })
  },

  // Get campaign analytics
  async getCampaignAnalytics(id: string, period: string = 'all_time'): Promise<CampaignAnalytics> {
    const headers = await getAuthHeaders()
    const response: CampaignAnalyticsApiResponse = await API.get(
      API_NAME,
      `/campaigns/${id}/analytics?period=${period}`,
      { headers }
    )
    return {
      campaignId: response.campaign_id,
      period: response.period,
      startDate: response.start_date,
      endDate: response.end_date,
      totalAmount: response.total_amount,
      raisedAmount: response.raised_amount,
      totalTransactions: response.total_transactions,
      byDonationType: (response.by_donation_type || []).map(dt => ({
        donationTypeId: dt.donation_type_id,
        amount: dt.amount,
        count: dt.count,
      })),
      byPlatform: (response.by_platform || []).map(p => ({
        platform: p.platform,
        amount: p.amount,
        count: p.count,
      })),
    }
  },
}

export default campaignsApi
